<template>
  <div class="xo-page" v-loading="loading">
    <div class="xo-page-header">
      <div class="xo-inner">
        <div class="xo-titlebar">
          <div>
            <h1 class="xo-title">Payments</h1>
            <p class="xo-subtitle">{{ resultSummary }}</p>
          </div>
          <button class="xo-btn xo-btn--navy" @click="downloadCSV">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              style="flex-shrink: 0"
            >
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
              />
              <polyline points="14 2 14 8 20 8" />
              <line x1="8" y1="13" x2="16" y2="13" />
              <line x1="8" y1="17" x2="16" y2="17" />
              <line x1="10" y1="9" x2="14" y2="9" />
            </svg>
            Export CSV
          </button>
        </div>
      </div>
    </div>

    <div class="xo-inner pg-list-wrap">
      <div class="pg-table">
        <div class="pg-thead">
          <div class="pg-th pg-th--txn">Transaction</div>
          <div class="pg-th pg-th--date">Date</div>
          <div class="pg-th pg-th--paytype">Pay Type</div>
          <div class="pg-th pg-th--desc">Description</div>
          <div class="pg-th pg-th--amount">Amount</div>
        </div>

        <div class="pg-tbody">
          <div v-for="(row, i) in paginatedData" :key="i" class="pg-row-wrap">
            <div class="pg-row">
              <div class="pg-td pg-td--txn">
                <span class="pg-ref">{{ row.txn }}</span>
              </div>
              <div class="pg-td pg-td--date">
                <span class="pg-td-muted">{{
                  transformDate(row.date_time)
                }}</span>
              </div>
              <div class="pg-td pg-td--paytype">
                <span
                  class="pg-pay-badge"
                  :class="getPayClass(row.pay_type_name)"
                >
                  <span class="pg-pay-dot"></span>{{ row.pay_type_name }}
                </span>
              </div>
              <div class="pg-td pg-td--desc">
                <span class="pg-td-text">{{ row.description }}</span>
              </div>
              <div class="pg-td pg-td--amount">
                <span class="pg-amount"
                  >KES {{ row.amount.toLocaleString() }}</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="xo-pager" v-if="!loading && totalPages > 1">
        <div class="xo-pager-info">
          Showing <strong>{{ rangeStart }}–{{ rangeEnd }}</strong> of <strong>{{ tableData.data.length }}</strong>
        </div>
        <div class="xo-pager-controls">
          <button class="xo-pager-btn" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)" aria-label="Previous page">‹</button>
          <template v-for="(p, idx) in pageButtons" :key="idx">
            <span v-if="p === '…'" class="xo-pager-ellipsis">…</span>
            <button v-else class="xo-pager-btn" :class="{ 'xo-pager-btn--active': p === currentPage }" @click="goToPage(p as number)">{{ p }}</button>
          </template>
          <button class="xo-pager-btn" :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)" aria-label="Next page">›</button>
        </div>
      </div>

      <div class="pg-empty" v-if="!loading && tableData.data.length === 0">
        <div class="pg-empty-icon">💳</div>
        <div class="pg-empty-text">No payments found</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, computed, watch } from "vue";
import router from "../../router";
import { ElMessage } from "element-plus";
import { useAuthStore } from "../../store/authStore";
import axios from "axios";
import { DateTime } from "luxon";
import { unparse } from "papaparse";
import { isCompanyAdminFromToken } from "../../utils/authRole";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

const ensureAdminAccess = () => {
  if (!isCompanyAdminFromToken(localStorage.getItem("_panda"))) {
    router.replace("/request");
    return false;
  }
  return true;
};

const loading = ref(false);
const tableData = reactive({ data: <any>[] });

// Client-side pagination — 10 rows per page. CSV export still grabs the full set.
const PAGE_SIZE = 10;
const currentPage = ref(1);
const totalPages = computed(() => Math.max(1, Math.ceil(tableData.data.length / PAGE_SIZE)));
const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE;
  return tableData.data.slice(start, start + PAGE_SIZE);
});
watch(totalPages, (n) => { if (currentPage.value > n) currentPage.value = n; });

const goToPage = (n: number) => {
  if (n < 1 || n > totalPages.value) return;
  currentPage.value = n;
  if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
};

const pageButtons = computed<(number | "…")[]>(() => {
  const total = totalPages.value;
  const cur = currentPage.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | "…")[] = [1];
  const left = Math.max(2, cur - 1);
  const right = Math.min(total - 1, cur + 1);
  if (left > 2) out.push("…");
  for (let i = left; i <= right; i++) out.push(i);
  if (right < total - 1) out.push("…");
  out.push(total);
  return out;
});

const rangeStart = computed(() =>
  tableData.data.length === 0 ? 0 : (currentPage.value - 1) * PAGE_SIZE + 1,
);
const rangeEnd = computed(() =>
  Math.min(currentPage.value * PAGE_SIZE, tableData.data.length),
);

// Header subtitle — same count line every list page carries.
const resultSummary = computed(() => {
  if (loading.value) return "Loading payments…";
  const total = tableData.data.length;
  return `${total} ${total === 1 ? "payment" : "payments"}`;
});

const postData = async () => {
  if (!ensureAdminAccess()) return;
  loading.value = true;
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_ACCOUNTING_SERVICE}customer_payments`,
      {
        company_id: store.getUserData.company_id,
        limit: 30,
        token: store.getToken,
      },
    );
    if (response.status == 200) tableData.data = response.data;
    else ElMessage({ message: "Failed to load payments.", type: "error" });
  } catch {
    ElMessage({ message: "Failed to load payments.", type: "error" });
  }
  loading.value = false;
};
void postData();

const transformDate = (date: string) =>
  DateTime.fromISO(date).toFormat("dd MMM yyyy, HH:mm");

const getPayClass = (type: string) => {
  if (!type) return "";
  const t = type.toLowerCase();
  if (t.includes("mpesa") || t.includes("mobile")) return "pg-pay--mpesa";
  if (t.includes("bank") || t.includes("transfer")) return "pg-pay--bank";
  if (t.includes("cash")) return "pg-pay--cash";
  return "pg-pay--default";
};

const downloadCSV = () => {
  if (!tableData.data.length) {
    ElMessage({ message: "No data to download", type: "warning" });
    return;
  }
  const csv = unparse(
    tableData.data.map((row: any) => ({
      Transaction: row.txn,
      Date: transformDate(row.date_time),
      "Pay Type": row.pay_type_name,
      Description: row.description,
      Amount: row.amount,
    })),
  );
  const link = document.createElement("a");
  link.href = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8;" }),
  );
  link.download = "payments.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap");
* {
  box-sizing: border-box;
}
/* Page chrome (header, buttons) lives in styles/list-page.scss — shared
   with Order History / Users / Statement. Only this page's table styles
   are below. */
.pg-list-wrap {
  padding-top: 4px;
  padding-bottom: 48px;
}
.pg-table {
  width: 100%;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}
.pg-thead {
  display: grid;
  grid-template-columns: 150px 160px 150px 1fr 140px;
  background: #f8fafc;
  border-bottom: 1.5px solid #e2e8f0;
  padding: 0 20px;
}
.pg-th {
  font-size: 0.68rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 12px 10px;
  white-space: nowrap;
}
.pg-th--amount {
  text-align: right;
}
.pg-tbody {
  display: flex;
  flex-direction: column;
}
.pg-row-wrap {
  border-bottom: 1px solid #f1f5f9;
}
.pg-row-wrap:last-child {
  border-bottom: none;
}
.pg-row {
  display: grid;
  grid-template-columns: 150px 160px 150px 1fr 140px;
  align-items: center;
  padding: 0 20px;
  height: 52px;
  transition: background 0.12s;
}
.pg-row:hover {
  background: #f8fafc;
}
.pg-td {
  padding: 0 10px;
  overflow: hidden;
}
.pg-ref {
  font-size: 0.84rem;
  font-weight: 700;
  color: #12284a;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}
.pg-td-text {
  font-size: 0.83rem;
  color: #334155;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}
.pg-td-muted {
  font-size: 0.81rem;
  color: #64748b;
  font-weight: 400;
  white-space: nowrap;
  display: block;
}
.pg-amount {
  font-size: 0.84rem;
  font-weight: 700;
  color: #12284a;
  display: block;
  text-align: right;
  white-space: nowrap;
}
.pg-pay-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.73rem;
  font-weight: 600;
  white-space: nowrap;
  border: 1.5px solid currentColor;
  text-transform: capitalize;
}
.pg-pay-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}
.pg-pay--mpesa {
  color: #15803d;
  background: #f0fdf4;
  border-color: #86efac;
}
.pg-pay--bank {
  color: #2563eb;
  background: #eff6ff;
  border-color: #93c5fd;
}
.pg-pay--cash {
  color: #d97706;
  background: #fffbeb;
  border-color: #fcd34d;
}
.pg-pay--default {
  color: #64748b;
  background: #f8fafc;
  border-color: #cbd5e1;
}
.pg-empty {
  text-align: center;
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.pg-empty-icon {
  font-size: 2.5rem;
}
.pg-empty-text {
  font-size: 1rem;
  font-weight: 500;
  color: #94a3b8;
}

</style>
